/**
 * Blog public index — `/blog`.
 *
 * Lists all published articles, filterable by tag. SEO-optimized page powered
 * by <Seo> component with hreflang + JSON-LD Blog schema.
 */
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Seo, organizationJsonLd } from "@/components/Seo";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { BookOpen, Search as SearchIcon, Tag, Clock, ArrowRight, Loader2 } from "lucide-react";

function formatDate(iso, locale) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(locale === "it" ? "it-IT" : locale === "pl" ? "pl-PL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function Blog() {
  const { t, locale } = useI18n();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 30 };
      if (activeTag) params.tag = activeTag;
      const r = await api.get("/blog/posts", { params });
      setPosts(r.data.posts || []);
      setTotal(r.data.total || 0);
      setTags(r.data.tags || []);
    } finally {
      setLoading(false);
    }
  }, [activeTag]);

  useImperativeLoad(() => { fetchPosts(); }, [activeTag]);

  const filtered = useMemo(() => {
    if (!q.trim()) return posts;
    const needle = q.trim().toLowerCase();
    return posts.filter((p) =>
      (p.title || "").toLowerCase().includes(needle) ||
      (p.meta_description || "").toLowerCase().includes(needle) ||
      (p.tags || []).some((tag) => tag.toLowerCase().includes(needle))
    );
  }, [posts, q]);

  const blogJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "NEURACOM Blog",
    description: t("blog.subtitle"),
    url: "https://neuracom.ai/blog",
    inLanguage: locale,
    publisher: {
      "@type": "Organization",
      name: "NEURACOM by TelcaVoIP",
    },
  };

  return (
    <div className="min-h-screen bg-[#0A0C1F] text-white" data-testid="blog-page">
      <Seo
        title={t("blog.seo_title")}
        description={t("blog.seo_description")}
        canonicalPath="/blog"
        ogImage="/brand/og-blog.png"
        jsonLdList={[organizationJsonLd, blogJsonLd]}
      />

      {/* Hero */}
      <section className="border-b border-slate-800 bg-gradient-to-br from-[#0B0E5C]/40 via-[#0A0C1F] to-[#05073B] px-6 py-16 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <Link to="/" className="mb-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-white" data-testid="blog-back-home">
            ← neuracom.ai
          </Link>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#9ACD32]/30 bg-[#9ACD32]/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#9ACD32]">
            <BookOpen className="h-3 w-3" /> {t("blog.pretitle")}
          </span>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" data-testid="blog-title">
            {t("blog.title")}
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {t("blog.subtitle")}
          </p>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="mx-auto max-w-6xl px-6 py-10 lg:px-12">
        <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("blog.search_ph")}
              className="border-slate-700 bg-slate-900 pl-9 text-white"
              data-testid="blog-search"
            />
          </div>
        </div>

        {tags.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="blog-tags">
            <button
              type="button"
              onClick={() => setActiveTag("")}
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                activeTag === "" ? "border-[#9ACD32] bg-[#9ACD32]/15 text-[#9ACD32]" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"
              }`}
              data-testid="blog-tag-all"
            >
              {t("blog.filter_all")}
            </button>
            {tags.map((tag) => (
              <button
                key={tag.tag}
                type="button"
                onClick={() => setActiveTag(tag.tag)}
                className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                  activeTag === tag.tag ? "border-[#9ACD32] bg-[#9ACD32]/15 text-[#9ACD32]" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"
                }`}
                data-testid={`blog-tag-${tag.tag}`}
              >
                #{tag.tag} <span className="ml-1 text-slate-500">{tag.count}</span>
              </button>
            ))}
          </div>
        )}

        {loading && (
          <p className="text-center text-sm text-slate-500" data-testid="blog-loading">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </p>
        )}

        {!loading && filtered.length === 0 && (
          <Card className="border-slate-800 bg-slate-900/60 p-12 text-center" data-testid="blog-empty">
            <BookOpen className="mx-auto h-10 w-10 text-slate-600" />
            <h3 className="mt-3 font-display text-lg font-bold text-white">{t("blog.empty_title")}</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">{t("blog.empty_body")}</p>
          </Card>
        )}

        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {filtered.map((post) => (
              <Link
                key={post.post_id}
                to={`/blog/${post.slug}`}
                className="block"
                data-testid={`blog-card-${post.slug}`}
              >
                <Card className="h-full border-slate-800 bg-slate-900/60 p-5 transition-all hover:-translate-y-1 hover:border-[#9ACD32]/40">
                  {post.hero_image_url ? (
                    <img
                      src={post.hero_image_url}
                      alt={post.title}
                      loading="lazy"
                      decoding="async"
                      className="mb-4 aspect-[16/9] w-full rounded-lg object-cover"
                    />
                  ) : (
                    <div className="mb-4 flex aspect-[16/9] w-full items-center justify-center rounded-lg bg-gradient-to-br from-[#0B0E5C]/60 to-[#05073B]">
                      <BookOpen className="h-10 w-10 text-[#9ACD32]/40" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                    <span>{formatDate(post.published_at, locale)}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" /> {post.reading_time_minutes} min
                    </span>
                  </div>
                  <h2 className="mt-2 line-clamp-2 font-display text-lg font-bold text-white group-hover:text-[#9ACD32]">
                    {post.title}
                  </h2>
                  <p className="mt-2 line-clamp-3 text-sm text-slate-400">
                    {post.meta_description}
                  </p>
                  {post.tags?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {post.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-0.5 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300">
                          <Tag className="h-2.5 w-2.5" /> {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#9ACD32]">
                    {t("blog.read_more")} <ArrowRight className="h-3 w-3" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {total > 0 && (
          <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-wider text-slate-500" data-testid="blog-count">
            {total} {total === 1 ? t("blog.article_singular") : t("blog.article_plural")}
          </p>
        )}
      </section>
    </div>
  );
}
